<template>
  <section>
    <Navbar />
    <main class="account-page responsive-pad">
        <div class="workspace-heading">
          <div><p class="eyebrow">Verwaltung</p><h1>Account</h1></div>
        </div>
        <section class="settings-card" aria-labelledby="account-planning-heading">
          <div class="settings-card-heading">
            <span class="settings-card-icon" aria-hidden="true">
              <font-awesome-icon :icon="['fas', 'cog']" />
            </span>
            <div>
              <h2 id="account-planning-heading">Planerstellung</h2>
              <p class="section-description">Vorgaben für die automatische Vertretungsplanung.</p>
            </div>
          </div>
          <div class="account-setting">
        <p class="mb-2">Mensa-Betreuung für die Klassen 9 und 10 in der 1. und 2. Fachstunde</p>
        <b-field class="mb-4">
          <b-switch :value="account.mensacare === true"
            :key="String(account.mensacare) + savingMensacare"
            :disabled="savingMensacare || !account.id"
            @input="saveMensacare">Aktiv</b-switch>
        </b-field>
          </div>
        </section>
        <section class="settings-card" aria-labelledby="account-publication-heading">
          <div class="settings-card-heading">
            <span class="settings-card-icon" aria-hidden="true">
              <font-awesome-icon :icon="['fas', 'eye']" />
            </span>
            <div>
              <h2 id="account-publication-heading">Veröffentlichung</h2>
              <p class="section-description">Gib jeden Tagesplan im Vertretungsplan einzeln frei.
                Nur veröffentlichte Pläne sind für Schülerinnen und Schüler sichtbar.</p>
            </div>
          </div>
          <b-button tag="router-link" to="/dashboard" class="primary-action">
            Zum Vertretungsplan
          </b-button>
        </section>
        <section class="settings-card" aria-labelledby="account-members-heading">
          <div class="settings-card-heading">
            <span class="settings-card-icon" aria-hidden="true">
              <font-awesome-icon :icon="['fas', 'users']" />
            </span>
            <div>
              <h2 id="account-members-heading">Benutzerverwaltung</h2>
              <p class="section-description">Mitglieder und Berechtigungen für diesen Account.</p>
            </div>
          </div>
        <b-table ref="table" v-if="showList" :key="(admins || []).length"
        class="app-table"
        :data="admins"
        :paginated="isPaginated"
        :per-page="perPage"
        :current-page.sync="currentPage"
        :pagination-simple="isPaginationSimple"
        :pagination-position="paginationPosition"
        :default-sort-direction="defaultSortDirection"
        :sort-icon="sortIcon"
        :sort-icon-size="sortIconSize"
        default-sort="lastname"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page">

          <b-table-column field="firstname" :label="$t('profile.name')" sortable
          v-slot="props">
            {{ props.row.firstname }}
          </b-table-column>

          <b-table-column field="lastname" :label="$t('profile.surname')" sortable
          v-slot="props">
            {{ props.row.lastname }}
          </b-table-column>

          <b-table-column field="rights.acc" :label="$t('account.account-management')"
          v-slot="props">
            <span v-on:change="change_rights(props.row.id, props.row.rights)">
              <b-checkbox v-model="props.row.rights.acc"></b-checkbox>
            </span>
          </b-table-column>

          <b-table-column field="rights.group" :label="$t('account.group-management')"
          v-slot="props">
            <span v-on:change="change_rights(props.row.id, props.row.rights)">
              <b-checkbox v-model="props.row.rights.group"></b-checkbox>
            </span>
          </b-table-column>

          <b-table-column field="rights.training" :label="$t('account.training-management')"
          v-slot="props">
            <span v-on:change="change_rights(props.row.id, props.row.rights)">
              <b-checkbox v-model="props.row.rights.training"></b-checkbox>
            </span>
          </b-table-column>

          <b-table-column field="rights.assignment" :label="$t('account.assignment-management')"
          v-slot="props">
            <span v-on:change="change_rights(props.row.id, props.row.rights)">
              <b-checkbox v-model="props.row.rights.assignment"></b-checkbox>
            </span>
          </b-table-column>

          <b-table-column v-slot="props">
            <button v-if="props.row.id !== acc_owner_id" class="button is-small is-danger">
              <font-awesome-icon class="icon" :icon="['fa', 'trash']"
              @click="del(props.row)"/>
            </button>
            <span v-else>owner</span>
          </b-table-column>
        </b-table>
        <div class="account-member-action">
          <AddMember v-on:added="refreshTable" :useCase="'admin'" />
        </div>
        </section>
    </main>
    <Footer />
  </section>
</template>

<script>
import AddMember from '../components/Clients/AddMember.vue';
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';

export default {
  components: {
    Navbar,
    Footer,
    AddMember,
  },

  created() {
    this.$store.dispatch('Account/load');
  },

  data() {
    return {
      savingMensacare: false,
      showList: true,
      isPaginated: true,
      isPaginationSimple: false,
      paginationPosition: 'bottom',
      defaultSortDirection: 'asc',
      sortIcon: 'arrow-up',
      sortIconSize: 'is-small',
      currentPage: 1,
      perPage: 10,
    };
  },

  computed: {
    account() {
      return this.$store.getters['Account/curr_acc'];
    },
    user() {
      return this.$store.getters['User/user'];
    },
    client() {
      return this.$store.getters['User/client'];
    },
    admins() {
      return this.$store.getters['Account/admins'];
    },
    acc_owner_id() {
      return this.$store.getters['Account/acc_owner'];
    },
  },

  methods: {
    async saveMensacare(value) {
      this.savingMensacare = true;
      try {
        const saved = await this.$store.dispatch('Account/save_mensacare', value);
        if (!saved) throw new Error('Could not save mensacare');
        this.$buefy.toast.open({ message: 'mensacare gespeichert.', type: 'is-success' });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'mensacare konnte nicht gespeichert werden.', type: 'is-danger',
        });
      } finally {
        this.savingMensacare = false;
      }
    },
    create_te() {
      this.$store.dispatch('Assignment/create_array');
    },
    temp_stats() {
      this.$store.dispatch('Assignment/temp_stats');
    },
    exportuserlinks() {
      this.$store.dispatch('User/exportuserlinks');
    },
    testImport() {
      this.$store.dispatch('User/testImport');
    },
    change_rights(userId1, rights1) {
      const payload = {
        userId: userId1,
        rights: rights1,
      };
      this.$store.dispatch('Account/change_rights', payload).then((response) => {
        if (response) {
          this.$buefy.toast.open({
            message: 'Admin hinzugefügt!',
            type: 'is-success',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Es konnte kein Benutzer gefunden oder angelegt werden.',
            type: 'is-danger',
          });
        }
      });
    },
    refreshTable() {
      this.$store.dispatch('Account/load');
    },
    del(user) {
      this.$buefy.dialog.confirm({
        title: this.$t('account.del-admin'),
        message: this.$t('account.del-text'),
        cancelText: this.$t('common.cancel'),
        confirmText: this.$t('account.del-admin'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch('Account/del_user', user.id).then((response) => {
            if (response) {
              this.$buefy.toast.open({ message: this.$t('account.del-success'), type: 'is-success' });
            }
          });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.account-page { padding-top: 32px; padding-bottom: 32px; }
.account-setting {
  padding-top: 20px;
  border-top: 1px solid var(--theme-border);
  font-size: .9375rem;
  .field { margin-bottom: 0 !important; }
}
.account-member-action { margin-top: 20px; }
@media (max-width: 600px) {
  .account-page { padding-top: 20px; padding-bottom: 20px; }
}
</style>
